/**
 * SUPABASE — kids-side wiring.
 *
 * The client itself moved to `packages/supabase` (one project, one client,
 * shared with `apps/student` — see that file's header). What stays here is the
 * part that is kids-only: registering the singleton into the legacy DI
 * `registry`, which ~89 files in this app read from.
 *
 * There is still exactly ONE client instance: the registry is handed the same
 * object the package creates.
 */
import { DatabaseModule, getSupabaseClient as pkgGetClient, waitForClient as pkgWaitForClient } from '@theidiomaclub/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';
import { registry } from './registry';

/** Helper for clean imports in repositories (synchronous — use only after auth boot). */
export function getSupabaseClient(): SupabaseClient | null {
    return registry.get('Supabase') || pkgGetClient();
}

/**
 * Async accessor — safe for fast-track public routes (placement-test,
 * enrollment landing). Registers into the DI registry on the way out, which is
 * why this app keeps its own wrapper instead of using the package's directly.
 */
export async function waitForClient(): Promise<SupabaseClient | null> {
    const client = await pkgWaitForClient();
    if (client && !registry.get('Supabase')) {
        registry.register('Supabase', client);
    }
    return client;
}

// Register via direct import (keeping window.supabaseClient for singleton safety)
if (!registry.get('Database')) {
    registry.register('Database', DatabaseModule);
    DatabaseModule.init().then((client) => {
        if (client) {
            console.log('✅ Database: Ready and Connected');
            registry.register('Supabase', client);
        } else {
            console.warn('⚠️ Database: Initialized with errors (check previous logs)');
        }
    });
}

export default DatabaseModule;
export type { DatabaseModuleType } from '@theidiomaclub/supabase';
